import { useTranslation } from 'react-i18next';
import ActivityTable from 'components/molecules/activity-table';
import UnactionedIcon from 'components/atoms/icons/unactioned-activity.svg?react';
import ActionedIcon from 'components/atoms/icons/actioned-activity.svg?react';
import { useReducer, useState, type FC } from 'react';
import {
  ModuleType,
  ActivityStatus,
  ReadingTemplateRoles,
} from 'infrastructure/enums';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import { useLocalStorage } from 'infrastructure/hooks/utils/use-local-storage';
import ExpandButton from 'components/molecules/expand-button';
import BaseButton from 'components/atoms/baseButton';
import useUser from 'utils/useUser';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import ActivityStatusSelectBox from 'components/molecules/activity-status-select-box';
import ActivityTableBulkModal from 'components/molecules/activity-table-bulk-modal';
import { usePatientActivity } from 'infrastructure/hooks/rpm/activity/use-patient-activity';
import PatientReadingTemplateModal from 'components/molecules/patient-reading-template';

import { useActivityBulkModal } from '../patient-activity/hooks/bulkComment/use-bulk-comment';
import s from './styles.module.scss';

interface IProps {
  patientGuid: string;
  type: ActivityStatus;
  tableId: string;
  onReadingsRefetch?: () => void;
}

const getTitle = (type: ActivityStatus) => {
  if (type === ActivityStatus.Actioned) {
    return 'labels.actionedActivity';
  }
  if (type === ActivityStatus.Unactioned) {
    return 'labels.unactionedActivity';
  }
  if (type === ActivityStatus.UnactionedNormalRisk) {
    return 'labels.unactionedActivityNormalRisk';
  }
  if (type === ActivityStatus.UnactionedCritical) {
    return 'labels.unactionedActivityCritical';
  }
};

const checkCurrentTemplateRole = (isMa: boolean, isRn: boolean) => {
  if (isMa) return ReadingTemplateRoles.MedicalAssistant;
  if (isRn) return ReadingTemplateRoles.RegisterNurse;
  return null;
};

const SinglePatientActivity: FC<IProps> = ({
  patientGuid,
  type,
  tableId,
  onReadingsRefetch,
}) => {
  const [pageSize, setPageSize] = useState(10);
  const { t } = useTranslation();
  const {
    isMobileDevice,
    isAgencyDoctor,
    isFullyManagedAgency,
    hasRNRole,
    isMedicalAssistantRole,
  } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();
  const isFullyManagedMa =
    isAgencyDoctor && isMedicalAssistantRole && isFullyManagedAgency;
  const isFullyManagedRn = isAgencyDoctor && hasRNRole && isFullyManagedAgency;
  const currentTemplateRole = checkCurrentTemplateRole(
    isFullyManagedMa,
    isFullyManagedRn,
  );

  const isActionedTable = type === 'actioned';
  const tableTypePrefix = isActionedTable ? 'a' : 'u';
  const titleIcon = isActionedTable ? (
    <ActionedIcon width={28} height={28} />
  ) : (
    <UnactionedIcon width={28} height={28} />
  );

  const {
    activity,
    queryKey,
    Alert,
    isLoading,
    isFetching,
    page,
    status,
    setPage,
    onSort,
    handleChangeType,
  } = usePatientActivity({
    patientGuid,
    type,
    pageSize,
    onReadingsRefetch,
  });

  const activityCount = activity?.count ?? 0;
  const titleLabel = getTitle(type);
  let title = `${t(titleLabel as any)}`;
  if (isAgencyDoctor) title += ` (${activityCount})`;
  const isNormalRisk =
    status === ActivityStatus.UnactionedNormalRisk &&
    type === ActivityStatus.UnactionedNormalRisk &&
    activityCount > 0;
  const showBulkCommentBtn =
    isNormalRisk && currentTemplateRole === null && !isReadOnlyRole;
  const showBulkTemplateCommentBtn =
    isNormalRisk && currentTemplateRole !== null && !isReadOnlyRole;

  const { value, setValue } = useLocalStorage(
    `${title}-table-opened-state`,
    true,
  );
  const { toggleOpen, opened } = useActivityBulkModal();
  const [openTemplateModal, toggleTemplateModal] = useReducer((v) => !v, false);

  return (
    <>
      <div className={s['single-wrapper']} id={tableId}>
        <div className={s.header}>
          <h4 className={s.title} onClick={() => setValue(!value)}>
            {titleIcon}
            {title}
          </h4>
          <div className={s['header-select']}>
            {showBulkCommentBtn && (
              <BaseButton
                label={t('labels.bulkReadingsComment')}
                dataCy="addNote-button"
                onClick={toggleOpen}
              />
            )}
            {showBulkTemplateCommentBtn && (
              <BaseButton
                label="Templates Modal"
                dataCy="addNote-button"
                onClick={toggleTemplateModal}
              />
            )}
            <>
              {isMobileDevice ? null : (
                <p data-cy="unactioned-activity-filter-label">
                  {t('labels.activitiesType')}:
                </p>
              )}
              <ActivityStatusSelectBox
                value={status}
                style={{ width: isMobileDevice ? 120 : 160 }}
                onChange={handleChangeType}
                type={type}
              />
            </>
            <ExpandButton
              expanded={value!}
              dataCy={`toggle-${title}-btn`}
              onClick={() => setValue(!value)}
            />
          </div>
        </div>
        <CollapseAnimation opened={value!} className={s.collapse}>
          <div className={s.divider} />
          <ActivityTable
            activity={activity}
            loading={isLoading || isFetching}
            page={page}
            setPage={setPage}
            setPerPage={setPageSize}
            itemsQueryKey={queryKey}
            status={status}
            view="patient"
            onSort={onSort}
            pageSize={pageSize}
            showSizeChanger
            tableId={tableId}
            tableTypePrefix={tableTypePrefix}
            module={ModuleType.RPMCCM}
            type={type}
          />
        </CollapseAnimation>
      </div>
      {Alert}
      {opened && (
        <ActivityTableBulkModal
          patientGuid={patientGuid}
          open={opened}
          onClose={toggleOpen}
          activityCount={activity?.count ?? 0}
        />
      )}
      {openTemplateModal && currentTemplateRole !== null && (
        <PatientReadingTemplateModal
          open={openTemplateModal}
          templateRole={currentTemplateRole}
          onClose={toggleTemplateModal}
          patientGuid={patientGuid}
          type={type}
          onReadingsRefetch={onReadingsRefetch}
        />
      )}
    </>
  );
};
export default SinglePatientActivity;
